import type { KycDocStatusItem, KycDocType, KycHistoryItem } from '../services/profile.service';

export const REQUIRED_KYC_DOC_TYPES = ['INE_FRONT', 'INE_BACK', 'ADDRESS_PROOF'] as const;

export type RequiredKycDocType = typeof REQUIRED_KYC_DOC_TYPES[number];

export const isRequiredKycDocType = (value: KycDocType): value is RequiredKycDocType =>
  REQUIRED_KYC_DOC_TYPES.includes(value as RequiredKycDocType);

export const hasAllRequiredKycDocs = (items: KycDocStatusItem[]) => {
  const map = new Map<KycDocType, KycDocStatusItem>();
  items.forEach((item) => map.set(item.docType, item));
  return REQUIRED_KYC_DOC_TYPES.every((type) => {
    const status = map.get(type)?.status;
    return status === 'PENDING' || status === 'APPROVED';
  });
};

export const parseUploadTypeFromNotes = (notes?: string | null) => {
  if (!notes) return null;
  const match = notes.match(/uploadType=(INE_FRONT|INE_BACK|ADDRESS_PROOF)/);
  return (match?.[1] as RequiredKycDocType | undefined) ?? null;
};

export const parseOriginalNameFromNotes = (notes?: string | null) => {
  if (!notes) return null;
  const match = notes.match(/originalName=([^;]+)/);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

export const parseMimeFromNotes = (notes?: string | null) => {
  if (!notes) return null;
  const match = notes.match(/mime=([^;]+)/);
  return match?.[1] ?? null;
};

export const parseSizeFromNotes = (notes?: string | null) => {
  if (!notes) return null;
  const match = notes.match(/size=([0-9]+)/);
  return match?.[1] ? Number(match[1]) : null;
};

export const filterHistoryByDocType = (items: KycHistoryItem[], docType: RequiredKycDocType) =>
  items.filter((item) => parseUploadTypeFromNotes(item.notes) === docType);
